import React, { useState, useRef, useEffect } from 'react';
import API_CONFIG from '../config';
import sessionManager from '../utils/sessionManager';
import './MessageThread.css';

const MessageThread = ({ conversation, messages, onSendMessage, onBack, userInfo, isMobile }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [creditReportStatus, setCreditReportStatus] = useState(null);
  const [sharingStatus, setSharingStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Check credit report status and sharing status on component mount
  useEffect(() => {
    checkCreditReportStatus();
    checkSharingStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ChatPage scrolling approach
  useEffect(() => {
    // Scroll to bottom when new messages arrive (like ChatPage)
    if (messages.length > 0) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  // Check if user has a credit report and its age
  const checkCreditReportStatus = async () => {
    try {
      const sessionData = sessionManager.getUserInfo();
      if (!sessionData?.authToken) return;
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREDIT_REPORTS_LIST}`, {
        headers: {
          'Authorization': `Bearer ${sessionData.authToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.reports && data.reports.length > 0) {
        const latestReport = data.reports[0];
        const reportDate = new Date(latestReport.report_date);
        const now = new Date();
        const daysDiff = Math.floor((now - reportDate) / (1000 * 60 * 60 * 24));
        
        setCreditReportStatus({
          hasReport: true,
          isRecent: daysDiff <= 90, // Within 90 days
          daysDiff: daysDiff,
          isExpired: latestReport.is_expired,
          reportDate: latestReport.report_date,
          creditScore: latestReport.credit_score
        });
      } else {
        setCreditReportStatus({
          hasReport: false,
          isRecent: false,
          daysDiff: null,
          isExpired: false
        });
      }
    } catch (error) {
      console.error('Error checking credit report status:', error);
      setCreditReportStatus({
        hasReport: false,
        isRecent: false,
        daysDiff: null,
        isExpired: false
      });
    }
  };

  // Check if credit report has been shared between users
  const checkSharingStatus = async () => {
    try {
      const sessionData = sessionManager.getUserInfo();
      if (!sessionData?.authToken || !conversation?.conversation_id) return;
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREDIT_REPORTS_SHARING_STATUS}?conversation_id=${conversation.conversation_id}`, {
        headers: {
          'Authorization': `Bearer ${sessionData.authToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSharingStatus({
          isShared: data.is_shared,
          reportId: data.report_id,
          reportDate: data.report_date,
          creditScore: data.credit_score,
          expiresAt: data.expires_at
        });
      } else {
        setSharingStatus({
          isShared: false
        });
      }
    } catch (error) {
      console.error('Error checking sharing status:', error);
      setSharingStatus({
        isShared: false
      });
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [newMessage]);



  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX

    try {
      const success = await onSendMessage(messageContent);
      if (!success) {
        // If sending failed, restore the message
        setNewMessage(messageContent);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore the message on error
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleShareCreditReport = async () => {
    try {
      const otherParticipant = getOtherParticipantInfo();
      
      if (!otherParticipant.id) {
        alert('Unable to identify the agent. Please try again.');
        return;
      }

      // If already shared, open credit report in new tab
      if (sharingStatus?.isShared) {
        window.open('/credit-report', '_blank');
        return;
      }

      // Call the CreditReport Lambda API to share the credit report
      const creditReportApiUrl = 'https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/credit-reports/share';
      const response = await fetch(creditReportApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.authToken}`
        },
        body: JSON.stringify({
          recipient_user_id: otherParticipant.id,
          share_type: 'landlord_application',
          expires_hours: 72
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Credit report shared successfully! The agent can now view your credit report.');
        
        // Update sharing status
        setSharingStatus({
          isShared: true,
          reportId: result.report_id,
          reportDate: result.report_date,
          creditScore: result.credit_score,
          expiresAt: result.expires_at
        });
        
        // Send a message to notify the agent
        await onSendMessage(`📊 I've shared my credit report with you. You can now view it using the "View Credit Report" button above.`);
      } else {
        alert(`❌ Failed to share credit report: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sharing credit report:', error);
      alert('❌ Failed to share credit report. Please try again.');
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    // Backend sends UTC timestamps without timezone info
    // We need to tell JavaScript this is UTC, then convert to local
    const messageTime = new Date(timestamp + 'Z'); // Add 'Z' to indicate UTC
    const now = new Date();
    const diffInDays = Math.floor((now - messageTime) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      // Today - show time only in local timezone
      return messageTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } else if (diffInDays === 1) {
      // Yesterday
      return `Yesterday ${messageTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit'
      })}`;
    } else if (diffInDays < 7) {
      // This week - show day and time
      return messageTime.toLocaleDateString([], { 
        weekday: 'short', 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } else {
      // Older - show date and time
      return messageTime.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit'
      });
    }
  };

  const formatDateSeparator = (timestamp) => {
    if (!timestamp) return '';
    
    // Backend sends UTC timestamps without timezone info
    const messageTime = new Date(timestamp + 'Z'); // Add 'Z' to indicate UTC
    const now = new Date();
    const diffInDays = Math.floor((now - messageTime) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return messageTime.toLocaleDateString([], { 
        weekday: 'long'
      });
    } else {
      return messageTime.toLocaleDateString([], { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric'
      });
    }
  };

  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!currentMessage?.created_at) return false;
    if (!previousMessage?.created_at) return true;

    // Backend sends UTC timestamps without timezone info
    const currentDate = new Date(currentMessage.created_at + 'Z');
    const previousDate = new Date(previousMessage.created_at + 'Z');
    
    // Show separator if messages are on different days
    return currentDate.toDateString() !== previousDate.toDateString();
  };

  const getMessageTypeIcon = (messageType) => {
    switch (messageType) {
      case 'application': return '📝';
      case 'system': return '🤖';
      case 'image': return '🖼️';
      default: return null;
    }
  };


  const getOtherParticipantInfo = () => {
    if (!conversation.other_participants || conversation.other_participants.length === 0) {
      return { name: 'Unknown User', role: 'user', id: null, phone: null };
    }
    const participant = conversation.other_participants[0];
    

    
    return {
      name: participant.name || 'Unknown User',
      role: participant.role || 'user',
      id: participant.id || participant.user_id || null,
      phone: participant.phone || participant.username || null
    };
  };

  const otherParticipant = getOtherParticipantInfo();

  return (
    <div className="message-thread">
      {/* Header */}
      <div className="message-thread-header">
        {isMobile && (
          <button className="back-button" onClick={onBack} title="Back to conversations">
            ← Back
          </button>
        )}
        
        {/* Credit Report Actions */}
        <div className="credit-report-actions">
          {/* View Credit Report for Agents/Landlords - only show if tenant has a report */}
          {(userInfo?.role === 'agent' || userInfo?.role === 'landlord' || userInfo?.role === 'property_manager' || 
            userInfo?.userRole === 'agent' || userInfo?.userRole === 'landlord' || userInfo?.userRole === 'property_manager') && 
           otherParticipant.role === 'tenant' && creditReportStatus?.hasReport && !creditReportStatus?.isExpired && (
            <div className="credit-report-section">
              <button 
                className="credit-report-link"
                onClick={() => {
                  // Open credit report in new tab with tenant identification
                  const params = new URLSearchParams({
                    conversation: conversation.conversation_id,
                    tenant: otherParticipant.name
                  });
                  
                  // Add tenant ID or phone if available for better identification
                  if (otherParticipant.id) {
                    params.set('tenant_id', otherParticipant.id);
                  }
                  if (otherParticipant.phone) {
                    params.set('tenant_phone', otherParticipant.phone);
                  }
                  
                  const creditReportUrl = `/credit-report?${params.toString()}`;
                  window.open(creditReportUrl, '_blank');
                }}
                title={`View ${otherParticipant.name}'s credit report`}
              >
                📊 View {otherParticipant.name}'s Credit Report
              </button>
            </div>
          )}
          
          {/* Share Credit Report for Tenants - only show if they have a recent report */}
          {(userInfo?.role === 'tenant' || userInfo?.userRole === 'tenant') && 
           (otherParticipant.role === 'agent' || otherParticipant.role === 'landlord' || otherParticipant.role === 'property_manager') && 
           creditReportStatus?.hasReport && creditReportStatus?.isRecent && !creditReportStatus?.isExpired && (
            <div className="credit-report-section">
              <button
                className="credit-report-share-button"
                onClick={handleShareCreditReport}
                title={sharingStatus?.isShared ? "View shared credit report" : "Share your credit report with this agent"}
              >
                {sharingStatus?.isShared ? "📊 View Shared Credit Report" : "🤝 Share Credit Report"}
              </button>
            </div>
          )}
          
          {/* Pull Credit Report for Tenants - show if they don't have a recent report */}
          {(userInfo?.role === 'tenant' || userInfo?.userRole === 'tenant') && 
           (otherParticipant.role === 'agent' || otherParticipant.role === 'landlord' || otherParticipant.role === 'property_manager') && 
           (!creditReportStatus?.hasReport || !creditReportStatus?.isRecent || creditReportStatus?.isExpired) && (
            <div className="credit-report-section">
              <button
                className="credit-report-pull-button"
                onClick={() => {
                  window.open('/profile', '_blank');
                }}
                title="Pull a new credit report"
              >
                📋 Pull Credit Report
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <div className="no-messages-content">
              <div className="no-messages-icon">💬</div>
              <p>No messages yet</p>
              <span>Start the conversation below</span>
            </div>
          </div>
        ) : (
          <div className="messages-list">
            {/* Reverse messages to show latest at bottom */}
            {[...messages].reverse().map((message, index, reversedMessages) => {
              // Simple comparison: is this message from the current user?
              const isOwnMessage = message.sender_id === userInfo?.id;
              const messageTypeIcon = getMessageTypeIcon(message.message_type);
              
              // Get previous message for date separator logic
              const previousMessage = index > 0 ? reversedMessages[index - 1] : null;
              const showDateSeparator = shouldShowDateSeparator(message, previousMessage);
              
              return (
                <React.Fragment key={message.id}>
                  {/* Date separator like iMessage */}
                  {showDateSeparator && (
                    <div className="date-separator">
                      <span className="date-text">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  
                  <div
                    className={`message ${isOwnMessage ? 'own-message' : 'other-message'} ${message.message_type}`}
                  >
                    <div className="message-content">
                      {/* Message type indicator */}
                      {messageTypeIcon && (
                        <div className="message-type-indicator">
                          {messageTypeIcon}
                        </div>
                      )}
                      
                      {/* Message text */}
                      <div className="message-text">
                        {message.content}
                      </div>
                      
                      {/* DDF indicator */}
                      {(message.is_from_ddf === 1 || message.is_from_ddf === true) && (
                        <div className="ddf-indicator">
                          <span title="Message sent via DDF API">📧 DDF</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Message metadata */}
                    <div className="message-metadata">
                      <div className="message-sender">
                        {!isOwnMessage && (
                          <span className="sender-name">
                            {message.sender_name}
                            {message.sender_role && (
                              <span className="sender-role"> ({message.sender_role})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="message-time">
                        {formatMessageTime(message.created_at)}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="message-input-container">
        <form onSubmit={handleSendMessage} className="message-input-form">
          <div className="input-wrapper">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="message-input"
              disabled={sending}
              rows="1"
            />
            <button
              type="submit"
              className={`send-button ${newMessage.trim() ? 'active' : ''}`}
              disabled={!newMessage.trim() || sending}
              title="Send message"
            >
              {sending ? (
                <div className="sending-spinner">⏳</div>
              ) : (
                '➤'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageThread;
